import { useQueryClient } from "@tanstack/react-query";
import FetchClients from "../client";
import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { EQUIPMENT_DETAILS_QUERY_KEY } from "./queryKeys";

const notes = { mutations: { usePost, usePatch, useDelete } } as const;
export default notes;

// endpoint requires {feature: "Equipment", access: "edit"}
async function postNote(
  equipmentId: number,
  note: string,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.equipment.POST(
    "/equipment/{equipment_id}/note",
    {
      params: {
        path: { equipment_id: equipmentId },
      },
      body: { body: note },
      headers: checkFeatureAccessHeader,
    }
  );

  if (data) return data;
  throw error;
}

function usePost(equipmentId: number) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Equipment", "edit"],
    (note: string, checkFeatureAccessHeader) =>
      postNote(equipmentId, note, checkFeatureAccessHeader),
    {
      onSettled: () =>
        queryClient.invalidateQueries({
          queryKey: [EQUIPMENT_DETAILS_QUERY_KEY, equipmentId],
        }),
    }
  );
}

// endpoint requires {feature: "Equipment", access: "edit"}
async function patchNote(
  equipmentId: number,
  noteId: number,
  note: string,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.equipment.PATCH(
    "/equipment/{equipment_id}/note/{note_id}",
    {
      params: {
        path: { equipment_id: equipmentId, note_id: noteId },
      },
      body: { body: note },
      headers: checkFeatureAccessHeader,
    }
  );

  if (data) return data;
  throw error;
}

function usePatch(equipmentId: number) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Equipment", "edit"],
    (
      { noteId, note }: { noteId: number; note: string },
      checkFeatureAccessHeader
    ) => patchNote(equipmentId, noteId, note, checkFeatureAccessHeader),
    {
      onSettled: () =>
        queryClient.invalidateQueries({
          queryKey: [EQUIPMENT_DETAILS_QUERY_KEY, equipmentId],
        }),
    }
  );
}

// endpoint requires {feature: "Equipment", access: "edit"}
async function deleteNote(
  equipmentId: number,
  noteId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { error } = await FetchClients.equipment.DELETE(
    "/equipment/{equipment_id}/note/{note_id}",
    {
      params: {
        path: { equipment_id: equipmentId, note_id: noteId },
      },
      headers: checkFeatureAccessHeader,
    }
  );
  if (error) throw error;
}

function useDelete(equipmentId: number) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Equipment", "edit"],
    (noteId: number, checkFeatureAccessHeader) =>
      deleteNote(equipmentId, noteId, checkFeatureAccessHeader),
    {
      onSettled: () =>
        queryClient.invalidateQueries({
          queryKey: [EQUIPMENT_DETAILS_QUERY_KEY, equipmentId],
        }),
    }
  );
}
